/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ChangeEvent,
  type FC,
  type FocusEventHandler,
  type MouseEvent,
  type ReactNode
} from 'react'
import Button, { ButtonProps } from 'react-bootstrap/Button'
import { getBase64, getImage, isImageValid, isResolutionValid, type ResolutionType } from './utils'


type FileItem = {
  file: File,
  dataURL: string
}

export type Props = {
  accept?: string
  /** When `video`, skips image resolution validation (use for MP4/WebM uploads). */
  mediaKind?: 'image' | 'video'
  multiple?: boolean
  onReceiveFiles: (files: FileItem[]) => void
  children?: ReactNode
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  disabled?: boolean,
  onBlur?: FocusEventHandler<HTMLButtonElement>
  className?: string
  style?: CSSProperties
  resolutionType?: ResolutionType
  resolutionWidth?: number
  resolutionHeight?: number
  onError?: (error: Error) => void,
  as?: React.ElementType
}

/**
 * FileUploader is described here.
 *
 * @example
 * ```jsx
 * <FileUploader />
 * ```
 *
 * @since 06/03/2024
 * @link https://react-styleguidist.js.org/docs/documenting.html
 */
const FileUploader: FC<Props> = ({
  accept,
  mediaKind = 'image',
  onReceiveFiles,
  onBlur,
  children,
  variant,
  size,
  disabled = false,
  className,
  style = {},
  as,
  multiple = false,
  resolutionType,
  resolutionWidth = 0,
  resolutionHeight = 1,
  onError = () => {},
}) => {
  const [files, setFiles] = useState<FileItem[]>([])
  const [error, setError] = useState<Error | null>(null)
  // Create a reference to the hidden file input element
  const hiddenFileInput = useRef<any>(null)
  // Programmatically click the hidden file input element
  // when the Button component is clicked
  const handleClick = (event: MouseEvent<HTMLButtonElement, MouseEvent>) => {
    hiddenFileInput.current.click()
  }
  // Call a function (passed as a prop from the parent component)
  // to handle the user-selected file
  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files === null) {
      return
    }

    setError(null)
    setFiles([])

    for (const file of event.target.files) {
      if (mediaKind === 'image' && isImageValid(file.type)) {
        const image = await getImage(file)

        if (
          resolutionType &&
          !isResolutionValid(image, resolutionType, resolutionWidth, resolutionHeight)
        ) {
          const error = new Error(`A resolução da imagem deve ser de ${resolutionWidth}x${resolutionHeight} pixels`)
          setError(error)
          onError(error)
          break
        }
      }

      const dataURL = await getBase64(file)
      setFiles((prev = []) => prev.concat({ file, dataURL }))
    }
  }

  useEffect(() => {
    if (files.length === 0) {
      return
    }

    onReceiveFiles(files)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  return (
    <>
      <Button
        as={as as any}
        onClick={handleClick as any}
        variant={variant}
        size={size}
        disabled={disabled}
        onBlur={onBlur}
        className={className}
        style={style}
      >
        {children ?? 'Enviar arquivo'}
      </Button>
      <ul style={{ listStyleType: 'binary' }}>
        {files?.map(file => (
          <li key={file.file.name}>
            {file.file.name}
          </li>
        ))}
      </ul>
      {error !== null && (
        <>
          <br/>
          <small style={{ color: 'red' }}>
            {error.message}
          </small>
          <br />
        </>
      )}
      {/* Hidden file input element */}
      <input
        type='file'
        accept={accept}
        onChange={handleChange}
        multiple={multiple}
        ref={hiddenFileInput}
        style={{ display: 'none' }}
      />
    </>
  )
}

export default FileUploader
